import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { 
  Database, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  RefreshCw,
  Upload,
  Image,
  User,
  Key
} from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'

interface DiagnosticResult {
  test: string
  success: boolean
  message: string
  details?: any
  fix?: string
}

export const DatabaseDiagnostic: React.FC = () => {
  const [results, setResults] = useState<DiagnosticResult[]>([])
  const [running, setRunning] = useState(false)
  const { user } = useAuth()

  const runDiagnostic = async () => {
    setRunning(true)
    const diagnosticResults: DiagnosticResult[] = []

    try {
      // Test 1: Vérification de l'authentification
      console.log('🔐 Test 1: Authentification...')
      if (user) {
        diagnosticResults.push({
          test: 'Authentification',
          success: true,
          message: `Utilisateur connecté: ${user.email}`,
          details: { userId: user.id, email: user.email }
        })
      } else {
        diagnosticResults.push({
          test: 'Authentification',
          success: false,
          message: 'Aucun utilisateur connecté',
          fix: 'Connectez-vous à votre compte admin'
        })
      }

      // Test 2: Connexion Supabase
      console.log('🔗 Test 2: Connexion Supabase...')
      try {
        const { data, error } = await supabase.auth.getSession()
        if (error) throw error
        
        diagnosticResults.push({
          test: 'Connexion Supabase',
          success: true,
          message: 'Connexion à Supabase établie',
          details: { sessionExists: !!data.session }
        })
      } catch (error: any) {
        diagnosticResults.push({
          test: 'Connexion Supabase',
          success: false,
          message: `Erreur de connexion: ${error.message}`,
          details: error,
          fix: 'Vérifiez votre configuration Supabase dans .env'
        })
      }

      // Test 3: Table blog_posts
      console.log('📋 Test 3: Table blog_posts...')
      try {
        const { data, error } = await supabase
          .from('blog_posts')
          .select('id')
          .limit(1)
        
        if (error) throw error
        
        diagnosticResults.push({
          test: 'Table blog_posts',
          success: true,
          message: 'Table blog_posts accessible',
          details: { recordCount: data?.length || 0 }
        })
      } catch (error: any) {
        diagnosticResults.push({
          test: 'Table blog_posts',
          success: false,
          message: `Table blog_posts inaccessible: ${error.message}`,
          details: error,
          fix: 'Exécutez le script fix-rls.sql dans votre dashboard Supabase'
        })
      }

      // Test 4: Table admin_users
      console.log('👤 Test 4: Table admin_users...')
      try {
        const { data, error } = await supabase
          .from('admin_users')
          .select('id')
          .limit(1)
        
        if (error) throw error
        
        diagnosticResults.push({
          test: 'Table admin_users',
          success: true,
          message: 'Table admin_users accessible',
          details: { recordCount: data?.length || 0 }
        })
      } catch (error: any) {
        diagnosticResults.push({
          test: 'Table admin_users',
          success: false,
          message: `Table admin_users inaccessible: ${error.message}`,
          details: error,
          fix: 'Créez la table admin_users avec le script fix-rls.sql'
        })
      }

      // Test 5: Bucket images
      console.log('🗂️ Test 5: Bucket images...')
      try {
        const { data, error } = await supabase.storage.listBuckets()
        if (error) throw error
        
        const blogImagesBucket = data?.find(bucket => bucket.name === 'blog-images')
        
        if (blogImagesBucket) {
          diagnosticResults.push({
            test: 'Bucket Images',
            success: true,
            message: 'Bucket blog-images existe',
            details: blogImagesBucket
          })
        } else {
          diagnosticResults.push({
            test: 'Bucket Images',
            success: false,
            message: 'Bucket blog-images introuvable',
            fix: 'Le bucket sera créé automatiquement au premier upload'
          })
        }
      } catch (error: any) {
        diagnosticResults.push({
          test: 'Bucket Images',
          success: false,
          message: `Erreur Storage: ${error.message}`,
          details: error,
          fix: 'Vérifiez les permissions Storage dans Supabase'
        })
      }

      // Test 6: Test d'insertion
      console.log('✏️ Test 6: Test insertion article...')
      if (user) {
        try {
          const testArticle = {
            title: 'Test Article ' + Date.now(),
            slug: 'test-article-' + Date.now(),
            excerpt: 'Test excerpt',
            content: 'Test content',
            image_url: '',
            category: 'Test',
            tags: ['test'],
            status: 'draft' as const,
            author_id: user.id,
            read_time: 1
          }

          const { data, error } = await supabase
            .from('blog_posts')
            .insert(testArticle)
            .select()
            .single()

          if (error) throw error

          // Nettoyer immédiatement
          await supabase.from('blog_posts').delete().eq('id', data.id)

          diagnosticResults.push({
            test: 'Test Insertion',
            success: true,
            message: 'Insertion d\'article réussie !',
            details: { testId: data.id }
          })
        } catch (error: any) {
          diagnosticResults.push({
            test: 'Test Insertion',
            success: false,
            message: `Impossible d'insérer: ${error.message}`,
            details: error,
            fix: 'Problème de politiques RLS - utilisez le script fix-rls.sql'
          })
        }
      }

      // Test 7: Test upload image
      console.log('🖼️ Test 7: Test upload image...')
      try {
        // Créer un fichier de test minimal
        const testBlob = new Blob(['test'], { type: 'text/plain' })
        const testFile = new File([testBlob], 'test.txt', { type: 'text/plain' })
        
        const { data, error } = await supabase.storage
          .from('blog-images')
          .upload(`test/${Date.now()}.txt`, testFile)

        if (error) {
          // Essayer de créer le bucket s'il n'existe pas
          if (error.message.includes('bucket')) {
            await supabase.storage.createBucket('blog-images', { public: true })
            
            diagnosticResults.push({
              test: 'Test Upload',
              success: false,
              message: 'Bucket créé, retry upload nécessaire',
              fix: 'Bucket créé automatiquement, l\'upload devrait maintenant fonctionner'
            })
          } else {
            throw error
          }
        } else {
          // Nettoyer le fichier de test
          await supabase.storage.from('blog-images').remove([data.path])
          
          diagnosticResults.push({
            test: 'Test Upload',
            success: true,
            message: 'Upload d\'image fonctionnel',
            details: { path: data.path }
          })
        }
      } catch (error: any) {
        diagnosticResults.push({
          test: 'Test Upload',
          success: false,
          message: `Upload impossible: ${error.message}`,
          details: error,
          fix: 'Vérifiez les politiques Storage et les permissions'
        })
      }

    } catch (globalError: any) {
      diagnosticResults.push({
        test: 'Diagnostic Global',
        success: false,
        message: `Erreur générale: ${globalError.message}`,
        details: globalError
      })
    }

    setResults(diagnosticResults)
    setRunning(false)

    // Afficher un résumé
    const failedTests = diagnosticResults.filter(r => !r.success)
    if (failedTests.length === 0) {
      toast.success('✅ Tous les tests réussis ! Votre blog devrait fonctionner.')
    } else {
      toast.error(`❌ ${failedTests.length} test(s) échoué(s). Voir les corrections ci-dessous.`)
    }
  }

  const fixAllIssues = async () => {
    setRunning(true)
    try {
      console.log('🔧 Correction automatique...')
      
      // 1. Créer le bucket s'il n'existe pas
      try {
        await supabase.storage.createBucket('blog-images', {
          public: true,
          allowedMimeTypes: ['image/*'],
          fileSizeLimit: 10485760
        })
        console.log('✅ Bucket créé')
      } catch (error) {
        console.log('ℹ️ Bucket existe déjà')
      }

      // 2. Ajouter l'utilisateur comme admin
      if (user) {
        try {
          await supabase
            .from('admin_users')
            .upsert({ 
              user_id: user.id, 
              email: user.email || 'admin@example.com' 
            })
          console.log('✅ Utilisateur ajouté comme admin')
        } catch (error) {
          console.log('ℹ️ Utilisateur déjà admin')
        }
      }

      toast.success('🔧 Corrections automatiques appliquées. Relancez le diagnostic.')
      
    } catch (error: any) {
      console.error('❌ Erreur lors des corrections:', error)
      toast.error(`Erreur: ${error.message}`)
    } finally {
      setRunning(false)
    }
  }

  useEffect(() => {
    // Lancer automatiquement le diagnostic au chargement
    runDiagnostic()
  }, [user])

  return (
    <div className="space-y-6">
      <div className="flex gap-4">
        <Button
          onClick={runDiagnostic}
          disabled={running}
          className="flex items-center gap-2"
        >
          {running ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <Database className="w-4 h-4" />
          )}
          {running ? 'Diagnostic en cours...' : 'Relancer le diagnostic'}
        </Button>
        
        <Button
          onClick={fixAllIssues}
          disabled={running}
          variant="outline"
          className="flex items-center gap-2"
        >
          <CheckCircle className="w-4 h-4" />
          Corrections automatiques
        </Button>
      </div>

      <div className="grid gap-4">
        {results.map((result, index) => (
          <Card key={index} className={result.success ? 'border-green-200' : 'border-red-200'}>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-sm">
                {result.success ? (
                  <CheckCircle className="w-4 h-4 text-green-600" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-600" />
                )}
                {result.test}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Alert variant={result.success ? "default" : "destructive"}>
                <AlertDescription>
                  <div className="space-y-2">
                    <p>{result.message}</p>
                    {result.fix && (
                      <div className="bg-blue-50 p-2 rounded text-sm">
                        <strong>Solution :</strong> {result.fix}
                      </div>
                    )}
                    {result.details && (
                      <details className="text-xs">
                        <summary className="cursor-pointer font-medium">Détails techniques</summary>
                        <pre className="mt-2 bg-gray-100 p-2 rounded overflow-x-auto">
                          {JSON.stringify(result.details, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        ))}
      </div>

      {results.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
              Actions recommandées
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2">Si les problèmes persistent :</h4>
                <ol className="list-decimal list-inside space-y-1 text-sm">
                  <li>Exécutez le script <code>fix-rls.sql</code> dans votre dashboard Supabase</li>
                  <li>Vérifiez que vous êtes bien connecté en tant qu'admin</li>
                  <li>Attendez quelques minutes pour la propagation des changements</li>
                  <li>Relancez ce diagnostic</li>
                </ol>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
} 